import { inject, Injectable } from '@angular/core';
import { Logger } from '@aiwyn/shared';
import { CRL_FILTERS_STORAGE_KEY, CrlFilterSelection } from '../models';
import { parseCrlSelection, serializeCrlSelection } from './utils/cxp-crl-filters.utils';

/**
 * Persists the CRL list-view filter selection (REQ-12) to `localStorage` under
 * the versioned `CRL_FILTERS_STORAGE_KEY`.
 *
 * Persistence is a side effect, so it lives in a service rather than the pure
 * filter helpers. Every read/write is wrapped so a malformed payload, a quota
 * error, or `localStorage` being unavailable can never crash page load — a bad
 * read resolves to an empty selection (the migration guard for a key/shape
 * change; bump the key's `vN` suffix to discard old payloads).
 */
@Injectable({ providedIn: 'root' })
export class CxpCrlFiltersService {
  private readonly logger = inject(Logger);

  /** Loads the persisted selection, or an empty set when absent/invalid. */
  loadSelection(): CrlFilterSelection {
    try {
      const raw = localStorage.getItem(CRL_FILTERS_STORAGE_KEY);
      if (!raw) return new Set();
      return parseCrlSelection(JSON.parse(raw));
    } catch (error) {
      this.logger.error('[CxpCrlFiltersService] loadSelection failed; resetting filters:', error);
      return new Set();
    }
  }

  /** Persists the selection; removes the key entirely when nothing is selected. */
  saveSelection(selection: CrlFilterSelection): void {
    try {
      if (selection.size === 0) {
        localStorage.removeItem(CRL_FILTERS_STORAGE_KEY);
        return;
      }
      localStorage.setItem(CRL_FILTERS_STORAGE_KEY, JSON.stringify(serializeCrlSelection(selection)));
    } catch (error) {
      this.logger.error('[CxpCrlFiltersService] saveSelection failed:', error);
    }
  }
}
